import { useState, useEffect, useCallback } from 'react'
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts'
import * as hl from '@nktkas/hyperliquid'

const transport = new hl.HttpTransport()
const hlInfo = new hl.InfoClient({ transport })

const AGENT_NAME = 'HypurrFiOrder'

export interface StoredAgent {
  privateKey: `0x${string}`
  address: string
}

function getStoredAgent(address: string): StoredAgent | null {
  try {
    const stored = localStorage.getItem(`hypurrfi_agent_${address}`)
    return stored ? JSON.parse(stored) : null
  } catch {
    return null
  }
}

function storeAgent(address: string, agent: StoredAgent): void {
  try {
    localStorage.setItem(`hypurrfi_agent_${address}`, JSON.stringify(agent))
  } catch (error) {
    console.error('Failed to store agent:', error)
  }
}

export interface UseWalletAgentResult {
  storedAgent: StoredAgent | null
  createAgentClient: (walletClient: any) => Promise<hl.ExchangeClient<any, any>>
}

export function useWalletAgent(account?: string): UseWalletAgentResult {
  const [storedAgent, setStoredAgent] = useState<StoredAgent | null>(null)

  useEffect(() => {
    if (account) {
      const agent = getStoredAgent(account)
      setStoredAgent(agent)
    }
  }, [account])

  const createAgentClient = useCallback(
    async (walletClient: any): Promise<hl.ExchangeClient<any, any>> => {
      if (!account) {
        throw new Error('Account is required')
      }

      const hlClient = new hl.ExchangeClient({
        wallet: walletClient,
        transport,
      })

      // Try to reuse existing agent or create new one
      let agentKey: `0x${string}`
      let agentAccount: ReturnType<typeof privateKeyToAccount>

      if (storedAgent) {
        // Reuse existing agent
        agentKey = storedAgent.privateKey
        agentAccount = privateKeyToAccount(agentKey)

        const userRole = await hlInfo.userRole({
          user: agentAccount.address as `0x${string}`,
        })

        if (userRole.role === 'agent') {
          return new hl.ExchangeClient({
            wallet: agentAccount,
            transport,
          })
        }
      }
      // Create new agent
      agentKey = generatePrivateKey()
      agentAccount = privateKeyToAccount(agentKey)

      // Store the new agent
      const newAgent: StoredAgent = {
        privateKey: agentKey,
        address: agentAccount.address,
      }

      storeAgent(account, newAgent)
      setStoredAgent(newAgent)

      await hlClient.approveAgent({
        agentAddress: agentAccount.address,
        agentName: AGENT_NAME,
      })

      return new hl.ExchangeClient({
        wallet: agentAccount,
        transport,
      })
    },
    [account, storedAgent],
  )

  return {
    storedAgent,
    createAgentClient,
  }
}
